import React, { useEffect, useState, useContext, useRef } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { useViewport } from '../../../hook/useViewport'; // Assuming this hook exists and works
import { ThemeContext } from '../../../context/2025/ThemeContext'; // Assuming this context exists
import { isTablet } from 'react-device-detect';

// --- Styles ---

// Global style to prevent body scrolling when the teaser is active
const GlobalAnimationStyle = createGlobalStyle`
  body.no-scroll, html.no-scroll {
    overflow: hidden !important;
  }
`;

// Styled Components
const LogoContainer = styled.div`
  width: 100%;
  max-width: 351px;
  margin: 0 auto;
  position: relative;
  @media (max-width: 768px) {
    max-width: 400px; /* Or adjust as needed for mobile logo size */
    margin: 0;
  }
`;

const MainTitle = styled.h1`
  color: #e3ff2f; /* Specific lime color */
  margin: 0;
  display: flex;
  align-items: center;
`;

const TeaserWrapper = styled.div`
  height: 100vh;
  width: 100%;
  position: relative; /* Establishes a positioning context */
  background-color: #000000;
  overflow: hidden; /* Prevents scrollbars within the component */
`;

const ContentWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex; /* Used for centering the inner layout div */
  justify-content: center;
  align-items: center;
  z-index: 10; /* Above the video's base layer if needed, but below interactive text */
  pointer-events: none; /* Let interactions pass through to TeaserWrapper */
`;

const StyledVideo = styled.video`
  position: absolute;
  top: ${props => (props.isMobile ? '-60px' : '-100px')}; /* Adjust based on your layout */
  left: 0;
  /* --- Sizing --- */
  /* Let height fill the container established by the invisible text */
  height: 140%;
  /* Width will be set via media queries below */
  width: auto; /* Default, will be overridden */
  max-width: 120%; /* Prevent exceeding container width if needed */
  object-fit: contain; /* Fit video within dimensions, preserve aspect ratio */
  object-position: left; /* Align video to the top left corner */
  /* --- Layering & Appearance --- */
  z-index: 6;
  opacity: ${props => props.opacity};
  transition: opacity 0.3s ease-out;
  will-change: opacity;
  pointer-events: none;

  /* --- Responsive Widths (MATCH THESE TO YOUR TEXT BLOCK SIZES) --- */

  /* Default (Mobile) Size - Inspect size below 'md' breakpoint */
  width: 800px; /* <<< EXAMPLE: Replace with inspected mobile width */

  /* Medium Screens (Tablet Size) - Matches 'md:' breakpoint in Tailwind (usually 768px) */
  @media (min-width: 768px) {
  // height: 180%;
  }

  /* Extra Large Screens (Desktop Size) - Matches 'xl:' breakpoint in Tailwind (usually 1280px) */
  @media (min-width: 1280px) {


  }

  /* Add other breakpoints (e.g., 'lg') if your text has intermediate sizes */
  /* @media (min-width: 1024px) { ... } */
`;

// --- Helper Function ---
const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

// --- Component Logic ---
const DarkTeaser = () => {
  // Hooks for viewport size and theme
  const { width } = useViewport();
  const isMobile = width <= 640;
  // const isTablet = width > 640 && width <= 1024; // Currently unused but kept for context
  // const isDesktop = width > 1024; // Currently unused
  const { setTheme } = useContext(ThemeContext);

  // Refs for DOM elements and state that doesn't trigger re-renders
  const videoRef = useRef(null);
  const wrapperRef = useRef(null); // Ref for the main TeaserWrapper to attach listeners
  const progressRef = useRef(0); // Stores current scroll progress (0-1)
  const touchStartYRef = useRef(0); // Stores Y position on touch start

  // State
  const [videoReady, setVideoReady] = useState(false); // Is video metadata loaded?
  const [contentOpacity, setContentOpacity] = useState(1); // Opacity for standard text (1 -> 0 -> 1)
  const [videoOpacity, setVideoOpacity] = useState(0);    // Opacity for the video animation (0 -> 1)

  // Effect: Set dark theme on mount
  useEffect(() => {
    setTheme('dark');
  }, [setTheme]);

  // Effect: Load video and check readiness
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    console.log('VIDEO EFFECT: Setting up video load listener');
    let cleanedUp = false; // Flag to prevent actions after cleanup

    const handleLoadedData = () => {
      if (cleanedUp) return;
      console.log('VIDEO EFFECT: Video metadata loaded. Duration:', video.duration);
      if (video.duration && isFinite(video.duration)) {
        video.pause();
        video.currentTime = 0; // Ensure it starts at the beginning
        progressRef.current = 0; // Reset progress ref
        setVideoReady(true); // Mark video as ready to enable scroll effect
        console.log('VIDEO EFFECT: Video ready.');
      } else {
        console.warn('VIDEO EFFECT: Video loaded but duration is invalid:', video.duration);
        // Consider adding fallback logic here if needed
      }
    };

    const handleError = (e) => {
        if (cleanedUp) return;
        console.error("VIDEO EFFECT: Error loading video", e);
        // Handle video load error (e.g., show fallback content)
    }

    video.addEventListener('loadedmetadata', handleLoadedData);
    video.addEventListener('error', handleError);

    // Check readyState in case event was missed (readyState 1 = HAVE_METADATA)
    if (video.readyState >= 1) {
      handleLoadedData();
    } else {
      video.load(); // Ensure video loading is triggered if not already started
    }

    // Cleanup function
    return () => {
      cleanedUp = true;
      console.log('VIDEO EFFECT: Cleaning up video listeners');
      video.removeEventListener('loadedmetadata', handleLoadedData);
      video.removeEventListener('error', handleError);
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  // Effect: Set up scroll/touch listeners and control animation
  useEffect(() => {
    // Wait until the video is ready and the wrapper element exists
    if (!videoReady || !wrapperRef.current || !videoRef.current) {
      console.log('SCROLL EFFECT: Aborting setup (deps not ready).', { videoReady, hasWrapper: !!wrapperRef.current, hasVideo: !!videoRef.current });
      return;
    }

    console.log('SCROLL EFFECT: Setting up scroll/touch listeners.');
    const video = videoRef.current;
    const wrapper = wrapperRef.current;

    // Prevent body scroll while this component is active
    document.documentElement.classList.add('no-scroll');
    document.body.classList.add('no-scroll');

    // --- Update Function ---
    // Calculates and sets video time and element opacities based on scroll progress
    const updateVideoAndOpacity = (newProgress) => {
      const clampedProgress = clamp(newProgress, 0, 1);
      progressRef.current = clampedProgress;

      // Update video time
      const video = videoRef.current;
      if (video && video.duration && isFinite(video.duration)) {
        const targetTime = clampedProgress * video.duration;
        if (Math.abs(video.currentTime - targetTime) > 0.01) {
             video.currentTime = targetTime;
        }
      }

      // --- Opacity Calculation ---
      // Define fade ranges (adjust these timings as needed)
      const fadeStart = 0.0; // Start fades immediately
      const textFadeOutEnd = 0.2; // Text fully faded out by 20%
      const textFadeInStart = 0.8; // Text starts fading back in at 80%
      const fadeEnd = 1.0; // Fades complete by 100%

      // Calculate opacity for TEXT (1 -> 0 -> 1)
      let currentContentOpacity;
      if (clampedProgress <= fadeStart) {
        currentContentOpacity = 1;
      } else if (clampedProgress <= textFadeOutEnd) { // Fading Out Text
        const fadeProgress = (clampedProgress - fadeStart) / (textFadeOutEnd - fadeStart);
        currentContentOpacity = 1 - clamp(fadeProgress, 0, 1);
      } else if (clampedProgress < textFadeInStart) { // Text Hidden
        currentContentOpacity = 0;
      } else if (clampedProgress <= fadeEnd) { // Fading In Text
        const fadeProgress = (clampedProgress - textFadeInStart) / (fadeEnd - textFadeInStart);
        currentContentOpacity = clamp(fadeProgress, 0, 1);
      } else { // Text Fully Visible End
        currentContentOpacity = 1;
      }
      setContentOpacity(currentContentOpacity);

      // Calculate opacity for VIDEO (0 -> 1 -> 0) - Inverse of text
      // Video fades in as text fades out, fades out as text fades in
      const videoFadeInEnd = textFadeOutEnd; // Video fade in syncs with text fade out
      const videoFadeOutStart = textFadeInStart; // Video fade out syncs with text fade in
      let currentVideoOpacity;

       if (clampedProgress <= fadeStart) {
          currentVideoOpacity = 0;
       } else if (clampedProgress <= videoFadeInEnd) { // Fading In Video
          const fadeProgress = (clampedProgress - fadeStart) / (videoFadeInEnd - fadeStart);
          currentVideoOpacity = clamp(fadeProgress, 0, 1);
       } else if (clampedProgress < videoFadeOutStart) { // Video Fully Visible
          currentVideoOpacity = 1;
       } else if (clampedProgress <= fadeEnd) { // Fading Out Video
          const fadeProgress = (clampedProgress - videoFadeOutStart) / (fadeEnd - videoFadeOutStart);
          currentVideoOpacity = 1- clamp(fadeProgress, 0, 1);
       } else { // Video Fully Hidden End
          currentVideoOpacity = 0;
       }
      setVideoOpacity(currentVideoOpacity);
    };

    // --- Event Handlers ---
    const handleWheel = (e) => {
      e.preventDefault(); // Prevent default page scrolling
      const scrollDelta = e.deltaY;
      // Adjust sensitivity: Higher value = less sensitive (more scroll needed)
      const sensitivity = 4000; // Needs tuning based on feel
      const progressChange = scrollDelta / sensitivity;
      updateVideoAndOpacity(progressRef.current + progressChange);
    };

    const handleTouchStart = (e) => {
      // Store the starting Y position of the touch
      touchStartYRef.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      e.preventDefault(); // Prevent default touch scroll/bounce
      const touchCurrentY = e.touches[0].clientY;
      // Calculate change in Y; moving finger up = positive delta = scroll down = increase progress
      const touchDelta = touchStartYRef.current - touchCurrentY;
      touchStartYRef.current = touchCurrentY; // Update start position for the next move event

      // Adjust sensitivity: Map touch movement (pixels) to progress (0-1)
      // Full viewport height swipe maps to full progress range (adjust if needed)
      const sensitivity = window.innerHeight;
      const progressChange = touchDelta / sensitivity;
      updateVideoAndOpacity(progressRef.current + progressChange);
    };

    // Add listeners to the main wrapper element
    wrapper.addEventListener('wheel', handleWheel, { passive: false });
    wrapper.addEventListener('touchstart', handleTouchStart, { passive: true }); // Can be passive
    wrapper.addEventListener('touchmove', handleTouchMove, { passive: false });

    // Set initial state (progress 0)
    updateVideoAndOpacity(progressRef.current);

    // --- Cleanup function ---
    return () => {
      console.log('SCROLL EFFECT: Cleaning up listeners...');
      wrapper.removeEventListener('wheel', handleWheel);
      wrapper.removeEventListener('touchstart', handleTouchStart);
      wrapper.removeEventListener('touchmove', handleTouchMove);
      // Allow body scroll again when component unmounts
      document.documentElement.classList.remove('no-scroll');
      document.body.classList.remove('no-scroll');
    };
  }, [videoReady]); // Re-run this effect ONLY when videoReady changes

  // --- Render ---
  return (
    <TeaserWrapper ref={wrapperRef}>
      {/* Inject global styles to prevent body scroll */}
      <GlobalAnimationStyle />

      {/* ContentWrapper centers the main content block */}
      <ContentWrapper style={{ opacity: 1 }}> {/* Keep parent opacity 1 */}

        {/* Inner layout grid (Tailwind) */}
        <div className='h-full md:max-w-[1440px] w-full flex flex-col md:flex-row justify-between md:gap-0 md:mx-auto md:items-center py-20 md:py-0 px-16 box-border'>

          {/* Right Column: Logo */}
          <LogoContainer className={`md:order-2 pb-12 md:pb-2 md:max-h-[691px] z-10`}>
            <svg
              /* Pass width/height directly for responsiveness */
              width={isMobile ? '144' : '351'}
              height={isMobile ? '288' : '691'}
              viewBox='0 0 351 691'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M175.515 0C298.788 0 351 48.3849 351 133.574C351 274.514 180.724 277.667 180.724 391.263C180.724 404.935 185.963 413.333 185.963 422.822C185.963 430.189 181.778 434.403 175.515 434.403C169.252 434.403 165.067 430.189 165.067 422.822C165.067 413.363 170.276 404.935 170.276 391.263C170.276 277.667 0 274.514 0 133.574C0 48.3849 52.2421 0 175.485 0H175.515ZM336.396 567.946C336.396 644.737 281.023 691 175.515 691C60.6129 691 14.6339 644.707 14.6339 567.946C14.6339 495.369 78.3481 452.259 175.515 452.259C272.682 452.259 336.396 495.369 336.396 567.946Z'
                fill='#DAFF01' // Lime color for logo
              />
            </svg>
          </LogoContainer>

          {/* Left Column: Text & Video Area */}
          {/* Needs 'relative' for the absolutely positioned video inside */}
          <div className='relative flex-1 md:order-1 flex flex-col md:h-[691px] md:min-h-[691px] md:max-h-[691px]'>

            {/* Top Text Block (Date, GBC, Instagram) */}
            {/* Apply contentOpacity for 1 -> 0 -> 1 fade */}
            {/* Added relative z-10 for layering safety */}
            <div className={`relative z-10 flex flex-col justify-start items-start gap-5 order-3 md:order-1`} >
              <div className='justify-start '>
                <span className='heading-4-bold text-white !text-[30px] lg:!text-[24px] xl:!text-[30px]'> May 1-2 <br /> </span>
                <span className='heading-3 text-white !text-[30px] lg:!text-[24px] xl:!text-[30px]'> George Brown College <br /> School of Design </span>
              </div>
              <a
                className='heading-3 text-lime !text-[34px] lg:!text-[28px] xl:!text-[34px] hover:scale-[1.04] transition-transform duration-300 hover:text-lime'
                href='https://www.instagram.com/sod_yes/'
                target='_blank'
                rel='noreferrer'
              > @sod_yes </a>
            </div>

            {/* Spacer (Desktop only) */}
             {/* Added relative z-10 for layering safety */}
            {!isMobile && <div className='relative z-10 flex-grow order-2'></div>}

            {/* Main Title / Video Block Area */}
            {/* Needs 'relative' to position the video inside it */}
            <div className={`relative flex flex-col order-1 md:order-3`}>

              {/* 1. Invisible Text (Provides layout size/spacing) */}
              <div style={{ opacity: contentOpacity }}>
                <MainTitle
                  className={ isMobile ? 'teaser-mb-h1 text-lime !text-[56px]' : 'teaser-h1 text-lime !text-[90px] !xl:text-[132px] ' }
                >
                  YES
                  <svg
                    width='0.5em' height='0.9em' viewBox='0 0 351 691' fill='none' xmlns='http://www.w3.org/2000/svg'
                    style={{ display: 'inline-block', marginTop: '0.03em' }}
                  > <path d='M175.515...' fill='#DAFF01' /> </svg> {/* Abbreviated path */}
                </MainTitle>
                <span className={ isMobile ? 'teaser-mb-h1 text-white flex !text-[56px]' : 'teaser-h1 text-white flex !text-[90px] !xl:text-[132px] ' }>
                  YEAR END
                </span>
                <span className={ isMobile ? 'teaser-mb-h1 text-white flex !text-[56px]' : 'teaser-h1 text-white flex !text-[90px] !xl:text-[132px] ' }>
                  SHOW <span className='text-lime'>25</span>
                </span>
              </div>

              {/* 2. Visible Video (Positioned over invisible text) */}
              {/* Apply videoOpacity for 0 -> 1 fade */}
              <StyledVideo
                ref={videoRef}
                src='/static/2025/vid/Teaser_animation-V3_1.mov' // Check path
                // Consider adding <source> tags for mp4/webm here
                // <source src="/path/to/video.webm" type="video/webm" />
                // <source src="/path/to/video.mp4" type="video/mp4" />
                muted
                playsInline
                preload='metadata' // 'metadata' is usually best for scrubbable videos
                disablePictureInPicture
                // style={{ height: isMobile ? '80%' : isTablet ? '90%' : '100%' }} // Adjust height based on device
                opacity={videoOpacity} // Controlled fade in
                isMobile={isMobile} // Pass mobile state for conditional styling
              />
            </div>
            {/* --- END OF TARGET BLOCK --- */}

          </div> {/* End Left Column */}
        </div> {/* End Inner layout div */}
      </ContentWrapper> {/* End ContentWrapper */}
    </TeaserWrapper>
  );
};

export default DarkTeaser;